import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import { IoIosSearch } from "react-icons/io";
import { LuRefreshCw } from "react-icons/lu";
import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { FaUpload } from "react-icons/fa";
import LoadingSpinner from "../../components/LoadingSpinner";


const FolioIndividualNotification = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [filters, setFilters] = useState({
        title: "",
        message: "",
        subscriber_name: "",
        mobileno: "",
        created_on: "",
        orderbycol: "t1.created_on",
        orderby: "desc",
    });
    const [appliedFilters, setAppliedFilters] = useState({
        title: "",
        message: "",
        subscriber_name: "",
        mobileno: "",
        created_on: "",
        orderbycol: "t1.created_on",
        orderby: "desc",
    });
    const [dateRange, setDateRange] = useState([null, null]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        showingRecords: "Showing 0 to 0 of 0 entries",
        limit: 100, // Added limit to show 10 records per page
    });
    const toastShownRef = useRef(false);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            const params = {
                ...appliedFilters,
                page: pagination.currentPage,
                segment: pagination.limit, // Send limit to API
            };

            const response = await axios.get(
                `${API_URL}FolioPushNotification/individual-notification`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data && Array.isArray(response.data.data)) {
                const data = response.data.data;
                const totalItems = response.data.total || 0;
                const totalPages = Math.ceil(totalItems / pagination.limit);

                const from = (pagination.currentPage - 1) * pagination.limit + 1;
                const to = Math.min(
                    pagination.currentPage * pagination.limit,
                    totalItems
                );

                setNotifications(data);
                setPagination((prev) => ({
                    ...prev,
                    totalPages,
                    totalItems,
                    showingRecords: `Showing ${from} to ${to} of ${totalItems} entries`,
                }));
            } else {
                setNotifications([]);
                setPagination((prev) => ({
                    ...prev,
                    showingRecords: "Showing 0 to 0 of 0 entries",
                    totalPages: 1,
                    currentPage: 1,
                }));
            }
        } catch (error) {
            console.error("Error fetching folio notifications:", error);
            setNotifications([]);
            showToast.error("Failed to fetch folio notifications");
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setDateRange(dates);

        let dateString = "";
        if (start && end) {
            dateString = `${start.toLocaleDateString(
                "en-GB"
            )} to ${end.toLocaleDateString("en-GB")}`;
        }

        setFilters((prev) => ({
            ...prev,
            created_on: dateString,
        }));
    };

    const handleSearch = () => {
        setAppliedFilters(filters);
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
    };

    const handleClearFilters = () => {
        const defaultFilterState = {
            title: "",
            message: "",
            created_on: "",
            orderbycol: "t1.created_on",
            orderby: "DESC",
        };

        setFilters(defaultFilterState);
        setAppliedFilters(defaultFilterState);
        setDateRange([null, null]);
    };

    const handleClearDate = () => {
        setDateRange([null, null]);
        setFilters((prev) => ({
            ...prev,
            created_on: "",
        }));
    };

    const handleSort = (column) => {
        const columnMap = {
            title: "t1.title",
            message: "t1.message",
            subscriber_name: "t2.fullname",
            mobileno: "t2.mobileno",
            created_on: "t1.created_on",
        };

        const mappedColumn = columnMap[column] || column;

        let sortOrder = "asc";
        if (mappedColumn === appliedFilters.orderbycol) {
            sortOrder = appliedFilters.orderby === "asc" ? "desc" : "asc";
        }

        const newFilters = {
            ...appliedFilters,
            orderbycol: mappedColumn,
            orderby: sortOrder,
        };

        setAppliedFilters(newFilters);
    };

    const handlePageChange = (direction) => {
        setPagination((prev) => {
            let newPage = prev.currentPage;
            if (direction === "prev" && prev.currentPage > 1) {
                newPage -= 1;
            } else if (direction === "next" && prev.currentPage < prev.totalPages) {
                newPage += 1;
            }
            return { ...prev, currentPage: newPage };
        });
    };

    const handleExport = async () => {
        try {
            setExportLoading(true);
            const token = localStorage.getItem("authToken");
            const exportParams = new URLSearchParams({
                ...appliedFilters,
                page: pagination.currentPage.toString(),
                segment: pagination.limit.toString(),
            });

            for (let [key, value] of exportParams.entries()) {
                if (!value || value === "") {
                    exportParams.delete(key);
                }
            }

            const exportUrl = `${API_URL}FolioPushNotification/individual-notification/export?${exportParams.toString()}`;
            const response = await fetch(exportUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const blob = await response.blob();
                const contentDisposition = response.headers.get("Content-Disposition");
                let filename = "folio-individual-notifications.xlsx";

                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                    if (filenameMatch) {
                        filename = filenameMatch[1];
                    }
                }

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                showToast.success("Export completed successfully");
            } else {
                throw new Error(`Export failed with status: ${response.status}`);
            }
        } catch (error) {
            console.error("Export error:", error);
            showToast.error("Failed to export notifications");
        } finally {
            setExportLoading(false);
        }
    };

    const getSortIcon = (column) => {
        const columnMap = {
            title: "t1.title",
            message: "t1.message",
            subscriber_name: "t2.fullname",
            mobileno: "t2.mobileno",
            created_on: "t1.created_on",
        };

        const mappedColumn = columnMap[column] || column;

        if (appliedFilters.orderbycol !== mappedColumn) return "sort";
        return appliedFilters.orderby === "asc" ? "sort-down" : "sort-up";
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB") + " " + date.toLocaleTimeString();
    };

    const getSubscriberName = (notification) => {
        if (notification.receiver_name) return notification.receiver_name;
        if (notification.user_fullname) return notification.user_fullname;
        return "";
    };

    const getMobileNumber = (notification) => {
        if (notification.mobileno) return notification.mobileno;
        if (notification.mobile_no) return notification.mobile_no;
        if (notification.user_mob) return notification.user_mob;
        return "";
    };

    useEffect(() => {
        fetchNotifications();
    }, [appliedFilters, pagination.currentPage]);

    return (
        <div className="page-container mt-5">
            <div className="row pb-2">
                <div className="col-md-3"></div>
                <div className="col-md-9 d-flex justify-content-end align-items-center">
                    <span className="me-3" style={{ fontSize: "0.8rem" }}>
                        {pagination.showingRecords}
                    </span>
                    <button
                        className="btn btn-default btn-sm me-2"
                        onClick={() => fetchNotifications()}
                    >
                        <LuRefreshCw />
                    </button>
                    <button
                        className="btn btn-default btn-sm me-2"
                        onClick={handleExport}
                        disabled={exportLoading}
                    >
                        {exportLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Exporting...
                            </>
                        ) : (
                            <>
                                <FaUpload /> Export
                            </>
                        )}
                    </button>
                    <button
                        className="btn btn-default btn-sm me-2"
                        onClick={() => handlePageChange("prev")}
                        disabled={pagination.currentPage === 1}
                        title="Previous"
                    >
                        <IoIosArrowBack />
                    </button>
                    <button
                        className="btn btn-default btn-sm"
                        onClick={() => handlePageChange("next")}
                        disabled={pagination.currentPage === pagination.totalPages}
                        title="Next"
                    >
                        <IoIosArrowForward />
                    </button>
                </div>
            </div>

            <div className="panel panel-default with-rounded-corner with-shadow p-10">
                <div className="panel-heading">
                    <span className="panel-title">Folio Individual Notification</span>
                </div>

                <div className="table-responsive" style={{ overflowX: "auto" }}>
                    <table
                        className="table table-condensed table-hover bg-white"
                        style={{ width: "100%", tableLayout: "fixed" }}
                    >
                        <colgroup>
                            <col style={{ width: "20%" }} />
                            <col style={{ width: "35%" }} />
                            <col style={{ width: "10%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "20%" }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th
                                    onClick={() => handleSort("title")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Notification Title{" "}
                                    <i className={`fa fa-${getSortIcon("title")}`}></i>
                                </th>
                                <th
                                    onClick={() => handleSort("message")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Description{" "}
                                    <i className={`fa fa-${getSortIcon("message")}`}></i>
                                </th>
                                <th
                                    onClick={() => handleSort("subscriber_name")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Receiver Name{" "}
                                    <i className={`fa fa-${getSortIcon("subscriber_name")}`}></i>
                                </th>
                                <th
                                    onClick={() => handleSort("mobileno")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Mobile No.{" "}
                                    <i className={`fa fa-${getSortIcon("mobileno")}`}></i>
                                </th>
                                <th
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleSort("created_on")}
                                >
                                    Date Time{" "}
                                    <i className={`fa fa-${getSortIcon("created_on")}`}></i>
                                </th>
                            </tr>
                            <tr>
                                <th>
                                    <input
                                        type="text"
                                        name="title"
                                        value={filters.title}
                                        onChange={handleFilterChange}
                                        className="form-control form-control-sm"
                                        placeholder="Filter title"
                                    />
                                </th>
                                <th>
                                    <input
                                        type="text"
                                        name="message"
                                        value={filters.message}
                                        onChange={handleFilterChange}
                                        className="form-control form-control-sm"
                                        placeholder="Filter message"
                                    />
                                </th>
                                <th>
                                    <input
                                        type="text"
                                        name="subscriber_name"
                                        value={filters.subscriber_name}
                                        onChange={handleFilterChange}
                                        className="form-control form-control-sm"
                                        placeholder="Filter name"
                                    />
                                </th>
                                <th>
                                    <div className="d-flex">
                                        <input
                                            type="text"
                                            name="mobileno"
                                            value={filters.mobileno}
                                            onChange={handleFilterChange}
                                            className="form-control form-control-sm me-2"
                                            placeholder="Filter mobile"
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-default btn-sm"
                                            onClick={handleSearch}
                                        >
                                            <IoIosSearch />
                                        </button>
                                    </div>
                                </th>
                                <th>
                                    <div className="d-flex align-items-center">
                                        <DatePicker
                                            selectsRange
                                            startDate={dateRange[0]}
                                            endDate={dateRange[1]}
                                            onChange={handleDateChange}
                                            isClearable={true}
                                            placeholderText="Filter date range"
                                            className="form-control form-control-sm me-2"
                                            monthsShown={2}
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            timeIntervals={15}
                                            dateFormat="dd/MM/yyyy HH:mm"
                                            popperPlacement="bottom-start"
                                            popperClassName="custom-datepickerr"
                                        />
                                        {dateRange[0] && (
                                            <button
                                                type="button"
                                                className="btn btn-default btn-sm me-2"
                                                onClick={handleClearDate}
                                            >
                                                Clear
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-default btn-sm"
                                            onClick={handleSearch}
                                        >
                                            <IoIosSearch />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger ms-2"
                                            style={{
                                                padding: "2px 6px",
                                                fontSize: "12px",
                                                lineHeight: "2",
                                            }}
                                            onClick={handleClearFilters}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 10 }).map((_, skeletonIndex) => (
                                    <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                                        {Array.from({ length: 5 }).map((_, colIndex) => (
                                            <td key={colIndex}>
                                                <div
                                                    className="skeleton-text"
                                                    style={{
                                                        width: "80%",
                                                        margin: "0 auto"
                                                    }}
                                                ></div>
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : notifications.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-3">
                                        <p className="fw-bold">No Records Found</p>
                                    </td>
                                </tr>
                            ) : (
                                notifications
                                    .map((notification, index) => (
                                        <tr key={notification.id || index}>
                                            <td
                                                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                                            >
                                                {notification.title}
                                            </td>
                                            <td
                                                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                                            >
                                                {notification.message}
                                            </td>
                                            <td
                                                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                                            >
                                                {getSubscriberName(notification)}
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                {getMobileNumber(notification)}
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                {formatDate(notification.date_time)}
                                            </td>
                                        </tr>
                                    ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div id="myModal" className="modal">
                <div className="modal-dialog panel panel-default panel-border top">
                    <div className="modal-content">
                        <div id="myModalContent"></div>
                    </div>
                </div>
            </div>

            <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
                <i className="ti-arrow-up"></i>
            </a>
        </div>
    );
};

export default FolioIndividualNotification;
